import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import httpClient from '../../components/helpers/httpClient';
import { toast } from 'react-toastify';
import './PurchaseOrderPDFPage.css';
import CircularProgress from '@mui/material/CircularProgress';
import { FiDownload, FiSend } from 'react-icons/fi';
import Logo from '../../assets/logos/Logo-Vitruvi.png';

// Helper function to format number with thousand separators using spaces
const formatNumber = (number) => {
  if (number === null || number === undefined || isNaN(number)) return '';
  return number.toFixed(2).replace('.', ',').replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const PurchaseOrderPDFPage = () => {
  const { poId } = useParams();
  const [searchParams] = useSearchParams();
  const pdfType = searchParams.get('type') || 'client'; // Default to client
  const [isLoading, setIsLoading] = useState(true);
  const [purchaseOrderData, setPurchaseOrderData] = useState(null);
  const [unitData, setUnitData] = useState(null);
  const [warnings, setWarnings] = useState([]);
  const [calculatedTotals, setCalculatedTotals] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setWarnings([]); // Reset warnings on fetch

        // Fetch purchase order data with type parameter
        const poResponse = await httpClient.get(
          import.meta.env.VITE_API_URL + `/api/purchase-orders/${poId}?detailed=true&type=${pdfType}`
        );

        // For client PDFs, also fetch totals
        let totalsResponse = null;
        if (pdfType === 'client') {
          totalsResponse = await httpClient.get(
            import.meta.env.VITE_API_URL + `/api/purchase-orders/${poId}/totals`
          );
        }

        if (poResponse.status === 200) {
          const responseData = { ...poResponse.data };
          
          // Extract purchase order data and lines from the nested structure
          let processedData;
          if (responseData.purchaseOrder) {
            // Data is nested under purchaseOrder
            processedData = {
              ...responseData.purchaseOrder,
              purchaseOrderLines: responseData.purchaseOrder.purchaseOrderLines || []
            };
          } else {
            // Data is at root level (fallback)
            processedData = {
              ...responseData,
              purchaseOrderLines: responseData.purchaseOrderLines || []
            };
          }
          
          // Extract unit data from the purchase order response
          // Try multiple possible paths for unit data
          let extractedUnitData = null;
          if (responseData.purchaseOrder?.unit_id) {
            extractedUnitData = responseData.purchaseOrder.unit_id;
          } else if (responseData.unit) {
            extractedUnitData = responseData.unit;
          } else if (processedData.unit_id) {
            extractedUnitData = processedData.unit_id;
          }
          
          // Log the response structure for debugging
          console.log('Purchase Order Response:', responseData);
          console.log('Processed Data:', processedData);
          console.log('Extracted Unit Data:', extractedUnitData);
          
          if (!extractedUnitData) {
            console.warn('Unit data not found in purchase order response');
            // Set a default unit data structure to prevent crashes
            extractedUnitData = {
              unit_name: 'Unknown Unit',
              address: 'Unknown Address',
              buyers: [],
              building_id: {
                project_id: {
                  project_commercial_name: 'Unknown Project'
                }
              }
            };
          } else {
            // Ensure buyers array exists, even if empty (only needed for client PDFs)
            if (pdfType === 'client' && !extractedUnitData.buyers) {
              extractedUnitData.buyers = [];
            }
          }
          
          setUnitData(extractedUnitData);

          const newWarnings = [];

          // For client PDFs, calculate lineBasePriceUndiscounted (pricing logic)
          if (pdfType === 'client' && processedData.purchaseOrderLines) {
            // Create a map of all order lines for easy lookup by id
            const allOrderLinesMap = new Map();
            processedData.purchaseOrderLines.forEach(category => {
              category.order_lines.forEach(line => {
                allOrderLinesMap.set(line.id, line);
              });
            });

            // Calculate lineBasePriceUndiscounted for each relevant order line (for display purposes)
            processedData.purchaseOrderLines.forEach(category => {
              category.order_lines.forEach(line => {
                line.lineBasePriceUndiscounted = 0; // Initialize

                // Only calculate for chosen options that aren't hidden from clients
                if (line.is_chosen_option && !line.is_hidden_for_client) {
                  if (line.is_free_line) {
                    // Use free_price directly for free lines
                    line.lineBasePriceUndiscounted = line.free_price || 0;
                  } else if (!line.is_standard_option) {
                    // It's an alternative or extra product
                    if (line.standard_option_id) {
                      // Alternative to a standard option - calculate difference
                      const standardOption = allOrderLinesMap.get(line.standard_option_id);
                      if (standardOption) {
                        const currentPriceIncl = line.price_details?.price_brut_incl_installation;
                        const standardPriceIncl = standardOption.price_details?.price_brut_incl_installation;
                        const currentPriceExcl = line.price_details?.price_brut_excl_installation;
                        const standardPriceExcl = standardOption.price_details?.price_brut_excl_installation;

                        if (currentPriceIncl !== undefined && standardPriceIncl !== undefined) {
                          line.lineBasePriceUndiscounted = currentPriceIncl - standardPriceIncl;
                        } else if (currentPriceExcl !== undefined && standardPriceExcl !== undefined) {
                          line.lineBasePriceUndiscounted = currentPriceExcl - standardPriceExcl;
                        } else {
                          newWarnings.push(`Missing price info for difference calc: ${line.product_name || `ID ${line.id}`} (vs std ID ${line.standard_option_id})`);
                        }
                      } else {
                        newWarnings.push(`Std option ID ${line.standard_option_id} not found for: ${line.product_name || `ID ${line.id}`}`);
                      }
                    } else {
                      // Standalone extra product - use its own price
                      const currentPriceIncl = line.price_details?.price_brut_incl_installation;
                      const currentPriceExcl = line.price_details?.price_brut_excl_installation;
                      if (currentPriceIncl !== undefined) {
                        line.lineBasePriceUndiscounted = currentPriceIncl;
                      } else if (currentPriceExcl !== undefined) {
                        line.lineBasePriceUndiscounted = currentPriceExcl;
                      } else {
                        newWarnings.push(`Missing price info for extra product: ${line.product_name || `ID ${line.id}`}`);
                      }
                    }
                  }
                  // Standard options have a base price of 0 for extra cost calculation
                }
              });
            });
          }

          // Update warnings and purchase order data
          setWarnings(newWarnings);
          setPurchaseOrderData(processedData);
        } else {
          toast.error('Failed to load purchase order data');
        }

        // Handle totals response (only for client PDFs)
        if (pdfType === 'client' && totalsResponse && totalsResponse.status === 200 && totalsResponse.data.status === 'success') {
          setCalculatedTotals(totalsResponse.data.totals);
        } else if (pdfType === 'supplier') {
          // For supplier PDFs, we don't need totals
          setCalculatedTotals({});
        } else if (pdfType === 'client') {
          toast.error('Failed to load purchase order totals');
        }

      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load purchase order data');
      } finally {
        setIsLoading(false);
      }
    };

    if (poId) {
      fetchData();
    }
  }, [poId, pdfType]);

  const handleDownloadPDF = async () => {
    try {
      toast.info('Generating PDF...');

      const response = await httpClient.get(
        import.meta.env.VITE_API_URL + `/api/purchase-orders/${poId}/generate-pdf?type=${pdfType}`,
        { responseType: 'blob' }
      );

      // Create a URL for the blob
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `purchase-order-${pdfType}-${poId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success('PDF downloaded successfully');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Failed to download PDF');
    }
  };

  const handleSendPDF = () => {
    // Placeholder for sending functionality
    toast.info('Send functionality will be implemented in the future');
  };

  // --- Component Render ---

  if (isLoading) {
    return (
      <div className="pdf-page-loading">
        <CircularProgress size={50} color="inherit" />
        <p>Loading purchase order data...</p>
      </div>
    );
  }

  if (!purchaseOrderData || (pdfType === 'client' && !calculatedTotals)) {
    return <div className="pdf-page-error">Failed to load purchase order data</div>;
  }

  // Add safety checks for unitData structure
  const safeUnitData = {
    unit_name: unitData?.unit_name || 'Unknown Unit',
    address: unitData?.address || 'Unknown Address',
    buyers: unitData?.buyers || [],
    building_id: unitData?.building_id || {},
  };

  const safeProjectData = {
    project_commercial_name: safeUnitData.building_id?.project_id?.project_commercial_name || 
                           unitData?.building_id?.project_id?.project_commercial_name ||
                           'Unknown Project'
  };

  // Get totals for client PDFs
  let totalExclVATUndiscounted, totalPercentageDiscountAmount, totalVat21Amount, totalVat6Amount, totalInclVAT, additionalDiscount, finalTotal, hasFixedDiscount;
  if (pdfType === 'client' && calculatedTotals) {
    ({
      total_excl_vat_undiscounted: totalExclVATUndiscounted,
      total_percentage_discount_amount: totalPercentageDiscountAmount,
      total_vat_21_amount: totalVat21Amount,
      total_vat_6_amount: totalVat6Amount,
      total_incl_vat: totalInclVAT,
      additional_discount: additionalDiscount,
      final_total: finalTotal
    } = calculatedTotals);

    // Eenmalige korting: the "additional_discount" if present
    hasFixedDiscount = additionalDiscount > 0;
  }

  return (
    <div className="pdf-page-container">
      <div className="pdf-actions">
        <button className="pdf-action-button download" onClick={handleDownloadPDF}>
          <FiDownload /> Download PDF
        </button>
        <button className="pdf-action-button send" onClick={handleSendPDF}>
          <FiSend /> Send to {pdfType === 'client' ? 'Client' : 'Supplier'}
        </button>
      </div>

      {warnings.length > 0 && (
        <div className="pdf-warnings">
          <h3>Warnings:</h3>
          <ul>
            {warnings.map((warning, index) => (
              <li key={index}>{warning}</li>
            ))}
          </ul>
        </div>
      )}

      <div className="pdf-content">
        <div className="pdf-header">
          <img src={Logo} alt="Logo" className="pdf-logo" />
          <div className="pdf-project-info">
            <h2>Project {safeProjectData.project_commercial_name}</h2>
            <div className="pdf-project-info-lines">
              <div className="clmn-1">
                <div className="pdf-project-info-line">
                  <p>Unit:</p>
                  <p>{safeUnitData.unit_name}</p>
                </div>
                <div className="pdf-project-info-line">
                  <p>Adres:</p>
                  <p>{safeUnitData.address}</p>
                </div>
                <div className="pdf-project-info-line">
                  <p>Datum:</p>
                  <p>{new Date().toLocaleDateString()}</p>
                </div>
              </div>

              {/* Only show client information for client PDFs */}
              {pdfType === 'client' && (
                <div className="clmn-2">
                  <div className="pdf-project-info-line">
                    <p>{safeUnitData.buyers.length > 1 ? 'Klanten' : 'Klant'}:</p>
                    <div> {/* Changed from p to div to allow multiple paragraphs */}
                      {safeUnitData.buyers.map((buyer, index) => (
                        <p key={index}>
                          {buyer.type === 'contact' ? `${buyer.first_name} ${buyer.last_name}` : buyer.company_name}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          <h1>{pdfType === 'client' ? 'Klantenkeuze' : 'Leverancier bestelling'}</h1>
        </div>

        <div className="pdf-body">
          {purchaseOrderData.purchaseOrderLines?.map((category, categoryIndex) => (
            <div key={categoryIndex} className="pdf-category">
              <h3>{categoryIndex + 1} - {category.category_name}</h3>
              <table className="pdf-table">
                {pdfType === 'client' ? (
                  // Client PDF table structure
                  <>
                    <colgroup>
                      <col style={{ width: '35%' }} />
                      <col style={{ width: '30%' }} />
                      <col style={{ width: '5%' }} />
                      <col style={{ width: '10%' }} />
                    </colgroup>
                    <thead>
                      <tr className="pdf-table-header">
                        <th>Product</th>
                        <th>Note</th>
                        <th>Basis</th>
                        <th>Meerprijs*</th>
                      </tr>
                    </thead>
                  </>
                ) : (
                  // Supplier PDF table structure
                  <>
                    <colgroup>
                      <col style={{ width: '25%' }} />
                      <col style={{ width: '20%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '8%' }} />
                      <col style={{ width: '10%' }} />
                      <col style={{ width: '8%' }} />
                    </colgroup>
                    <thead>
                      <tr className="pdf-table-header">
                        <th>Product</th>
                        <th>Note</th>
                        <th>Product ID</th>
                        <th style={{ width: '8%' }}>Aantal</th>
                        <th style={{ width: '10%' }}>Aantal Incl. Marge</th>
                        <th style={{ width: '8%' }}>Eenheid</th>
                      </tr>
                    </thead>
                  </>
                )}
                <tbody>
                  {category.order_lines.map((orderLine, index) => {
                    // For client PDFs: only show chosen options not hidden from client
                    // For supplier PDFs: show all chosen options (including hidden from client)
                    if (!orderLine.is_chosen_option || (pdfType === 'client' && orderLine.is_hidden_for_client)) {
                      return null;
                    }

                    if (pdfType === 'client') {
                      // Client PDF row rendering
                      const quantity = orderLine.quantity_total || orderLine.quantity || 1;
                      const lineTotalUndiscounted = (orderLine.lineBasePriceUndiscounted !== undefined && orderLine.lineBasePriceUndiscounted !== null)
                        ? orderLine.lineBasePriceUndiscounted * quantity
                        : 0; // Calculate total undiscounted price for the line

                      // Determine what to display in the Meerprijs column
                      const displayPrice = (lineTotalUndiscounted !== 0) ? `€${formatNumber(lineTotalUndiscounted)}` : '-';

                      return (
                        <tr key={index}>
                          <td>
                            <div className="pdf-product-name">
                              {orderLine.is_free_line ? orderLine.free_product_name : orderLine.product_name}
                              {((orderLine.is_free_line && orderLine.free_product_name) || (!orderLine.is_free_line && orderLine.product_name)) && (orderLine.is_free_line ? orderLine.free_product_brand : orderLine.product_brand) && ' - '}
                              {orderLine.is_free_line ? orderLine.free_product_brand : orderLine.product_brand}
                            </div>
                            <div className="pdf-product-description" style={{ fontSize: '0.9em', paddingLeft: '10px' }}>
                              <i>{orderLine.product_description}</i>
                            </div>
                          </td>
                          <td>{orderLine.note_client}</td>
                          <td>{orderLine.is_standard_option ? '✓' : '-'}</td>
                          <td>{displayPrice}</td>
                        </tr>
                      );
                    } else {
                      // Supplier PDF row rendering
                      return (
                        <tr key={index}>
                          <td>
                            <div className="product-info">
                              <div className="product-name-brand">
                                {orderLine.is_free_line ? orderLine.free_product_name : orderLine.product_name}
                                {((orderLine.is_free_line && orderLine.free_product_name) || (!orderLine.is_free_line && orderLine.product_name)) && (orderLine.is_free_line ? orderLine.free_product_brand : orderLine.product_brand) && ' - '}
                                {orderLine.is_free_line ? orderLine.free_product_brand : orderLine.product_brand}
                              </div>
                              <div className="product-description">
                                {orderLine.is_free_line ? orderLine.free_product_description : orderLine.product_description}
                              </div>
                            </div>
                          </td>
                          <td>{orderLine.note_supplier || ''}</td>
                          <td>{orderLine.SKU_supplier || ''}</td>
                          <td>{formatNumber(orderLine.quantity)}</td>
                          <td>{formatNumber((orderLine.quantity || 0) + (orderLine.quantity_margin || 0))}</td>
                          <td>{orderLine.unit || ''}</td>
                        </tr>
                      );
                    }
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>

        {/* Only show signature and totals for client PDFs */}
        {pdfType === 'client' && (
          <div className="pdf-signature">
            <div className="pdf-signature-container">
              <div className="pdf-signature-left">
                <p>Akkoord van klant</p>
              </div>
              <div className="pdf-signature-right">
                {/* Display calculated totals */}
                <p style={{ fontWeight: 'bold' }}>Totaal Excl. BTW:</p>
                <p style={{ fontWeight: 'bold' }}>€{formatNumber(totalExclVATUndiscounted)}</p>

                {/* Only show discount row if there are discounts */}
                {totalPercentageDiscountAmount > 0 && (
                  <>
                    <p>Kortingen (%):</p>
                    <p>- €{formatNumber(totalPercentageDiscountAmount)}</p> {/* Show as negative */}
                  </>
                )}

                {/* Show VAT rows only if applicable */}
                {totalVat21Amount > 0 && (
                  <>
                    <p>BTW 21%:</p>
                    <p>€{formatNumber(totalVat21Amount)}</p>
                  </>
                )}
                {totalVat6Amount > 0 && (
                  <>
                    <p>BTW 6%:</p>
                    <p>€{formatNumber(totalVat6Amount)}</p>
                  </>
                )}

                <p style={{ marginTop: '10px', fontWeight: 'bold' }}>Totaal Incl. BTW:</p>
                <p style={{ marginTop: '10px', fontWeight: 'bold' }}>€{formatNumber(totalInclVAT)}</p>

                {/* Show fixed discount and final total only if fixed discount exists */}
                {hasFixedDiscount && (
                  <>
                    <p>Eenmalige korting:</p>
                    <p>- €{formatNumber(additionalDiscount)}</p> {/* Show as negative */}
                    <p style={{ marginTop: '10px', fontWeight: 'bold' }}>Eindtotaal:</p>
                    <p style={{ marginTop: '10px', fontWeight: 'bold' }}>€{formatNumber(finalTotal)}</p>
                  </>
                )}
                {/* If no fixed discount, the final total is the same as totalInclVAT */}
                {!hasFixedDiscount && (
                  <>
                    <p style={{ marginTop: '10px', fontWeight: 'bold' }}>Eindtotaal:</p>
                    <p style={{ marginTop: '10px', fontWeight: 'bold' }}>€{formatNumber(finalTotal)}</p>
                  </>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Only show footer note for client PDFs */}
        {pdfType === 'client' && (
          <div className="pdf-footer">
            <p>* Meerprijs Excl. BTW en exclusief kortingen.</p> {/* Updated footer note */}
          </div>
        )}
      </div>
    </div>
  );
};

export default PurchaseOrderPDFPage; 
